import Cookies from 'js-cookie'
import { removeAuthToken, setAuthToken } from '@/libs/graphql-client'
import { UserServiceUserGqlOutput } from '@/types/user'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const ACCESS_TOKEN = 'zma_access_token'
export const REFRESH_TOKEN = 'zma_refresh_token'
const BACKUP_ACCESS_TOKEN = 'backupAccessToken'

interface AuthState {
  auth: {
    user: UserServiceUserGqlOutput | null
    accessToken: string
    refreshToken: string
    isAuthenticated: boolean
    isImpersonating: boolean
    originalUser: UserServiceUserGqlOutput | null
    setTokens: (accessToken: string, refreshToken: string) => void
    setUser: (user: UserServiceUserGqlOutput | null) => void
    setImpersonation: (
      isImpersonating: boolean,
      originalUser?: UserServiceUserGqlOutput | null
    ) => void
    restoreFromImpersonation: () => void
    reset: () => void
    initializeAuth: () => void
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      const initializeAuth = () => {
        // const accessToken = Cookies.get(ACCESS_TOKEN) || ''
        // const refreshToken = Cookies.get(REFRESH_TOKEN) || ''
        const accessTokenLocal = localStorage.getItem(ACCESS_TOKEN) || ''
        const refreshTokenLocal = localStorage.getItem(REFRESH_TOKEN) || ''

        if (accessTokenLocal) {
          setAuthToken(accessTokenLocal)
        }

        set((state) => ({
          ...state,
          auth: {
            ...state.auth,
            accessToken: accessTokenLocal,
            refreshToken: refreshTokenLocal,
            isAuthenticated: !!accessTokenLocal,
          },
        }))
      }

      return {
        auth: {
          user: null,
          accessToken: '',
          refreshToken: '',
          isAuthenticated: false,
          isImpersonating: false,
          originalUser: null,

          setTokens: (accessToken: string, refreshToken: string) => {
            // Set cookies
            Cookies.set(ACCESS_TOKEN, accessToken, { expires: 7 }) // 7 days
            Cookies.set(REFRESH_TOKEN, refreshToken, { expires: 30 }) // 30 days
            localStorage.setItem(ACCESS_TOKEN, accessToken)
            localStorage.setItem(REFRESH_TOKEN, refreshToken)
            // Set GraphQL client auth header
            setAuthToken(accessToken)
            // setApolloAuthToken(accessToken)
            set((state) => ({
              ...state,
              auth: {
                ...state.auth,
                accessToken,
                refreshToken,
                isAuthenticated: true,
              },
            }))
          },

          setUser: (user: UserServiceUserGqlOutput | null) =>
            set((state) => ({ ...state, auth: { ...state.auth, user } })),

          setImpersonation: (
            isImpersonating: boolean,
            originalUser?: UserServiceUserGqlOutput | null
          ) =>
            set((state) => ({
              ...state,
              auth: {
                ...state.auth,
                isImpersonating,
                originalUser: originalUser || state.auth.originalUser,
              },
            })),

          restoreFromImpersonation: () => {
            const backupToken = localStorage.getItem(BACKUP_ACCESS_TOKEN)
            if (backupToken) {
              // Restore the backup token
              Cookies.set(ACCESS_TOKEN, backupToken, { expires: 7 })
              setAuthToken(backupToken)

              // Remove backup token from localStorage
              localStorage.removeItem(BACKUP_ACCESS_TOKEN)

              set((state) => ({
                ...state,
                auth: {
                  ...state.auth,
                  accessToken: backupToken,
                  isImpersonating: false,
                  user: state.auth.originalUser,
                  originalUser: null,
                },
              }))
            }
          },

          reset: () => {
            // Remove cookies
            Cookies.remove(ACCESS_TOKEN)
            Cookies.remove(REFRESH_TOKEN)
            localStorage.removeItem(ACCESS_TOKEN)
            localStorage.removeItem(REFRESH_TOKEN)

            // Remove backup token if exists
            localStorage.removeItem(BACKUP_ACCESS_TOKEN)

            // Remove GraphQL client auth header
            removeAuthToken()

            set((state) => ({
              ...state,
              auth: {
                ...state.auth,
                user: null,
                accessToken: '',
                refreshToken: '',
                isAuthenticated: false,
                isImpersonating: false,
                originalUser: null,
              },
            }))
          },

          initializeAuth,
        },
      }
    },
    {
      name: 'auth-storage',
      partialize: (state) => ({
        auth: {
          user: state.auth.user,
          isImpersonating: state.auth.isImpersonating,
          originalUser: state.auth.originalUser,
        },
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<AuthState>
        return {
          ...currentState,
          auth: {
            ...currentState.auth,
            ...persisted?.auth,
          },
        }
      },
    }
  )
)

// Initialize auth on store creation
useAuthStore.getState().auth.initializeAuth()

export const useAuth = () => useAuthStore((state) => state.auth)
